<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="tempFilter['pages']"
            :pages-items="pagesFormatted"
            :clearable="displayClearable('pages')"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>

        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :channels="channels"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
      <ModalAlert
        v-if="showTikTokAlert"
        :alert-message="alertMessage"
        :link-text="linkText"
        :link-reference="linkReference"
        @close-alert="closeAlert()"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import { DsIcon } from '@ds/icons';
import PageFilter from './PageFilter';
import NegativationFilter from './NegativationFilter';
import PolarityFilter from './PolarityFilter';
import StatusFilter from './StatusFilter';
import SacTypeFilter from './SacTypeFilter';
import UserFilter from './UserFilter';
import DarkPostFilter from './DarkPostFilter';
import CheckedPosts from './CheckedPosts';
import RepliersFilter from './RepliersFilter';
import sacFilterModel from '@/mixins/sac-filter-model';
import filtersModel from '@/mixins/filters-model';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import ModalAlert from '@/components/ModalAlert';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { isEqual } from 'lodash';

import { Flags } from '@/plugins/rollout';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';
import EventBus from '@/utils/event-bus';

const SHOW_TIKTOK_ALERT = [
  'PropSentimentTimeSeries',
  'PropSentimentGauge',
  'PropBaseShareSentiment',
  'EngagementMatrix',
];

export default {
  name: 'FilterSacModal',
  components: {
    StIcon,
    DsIcon,
    PageFilter,
    PolarityFilter,
    StatusFilter,
    SacTypeFilter,
    UserFilter,
    DarkPostFilter,
    CheckedPosts,
    RepliersFilter,
    NegativationFilter,
    GlobalFilterSummary,
    ModalAlert,
  },
  mixins: [
    sacFilterModel,
    filtersModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      channels: [],
      globalFilterByDataType: {},
      globalFilterByDataTypeToShow: {},
      changeOnPageFilter: false,
      tikTokAlertRule: false,
      showTikTokAlert: false,
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.sac.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    filtersObj() {
      const themeItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacFilter.notClassified'),
          value: 'Não Classificados',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.themes?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const tagItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacFilter.notClassified'),
          value: 'Sem Tags',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.tags?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const namesItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacFilter.noGroups'),
          value: 'Sem Grupos',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.names?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      return [
        {
          component: 'SacTypeFilter',
          filterType: FILTER_NAMES.SAC_TYPE,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_TYPE),
          },
        },
        {
          component: 'StatusFilter',
          filterType: FILTER_NAMES.SAC_STATUS,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_STATUS),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.GROUP,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacFilter.groups'),
            selectItems: namesItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.GROUP),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.THEME,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacFilter.themes'),
            selectItems: themeItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.TAG,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacFilter.tags'),
            selectItems: tagItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
            hasTooltip: true,
          },
        },
        {
          component: 'PolarityFilter',
          filterType: FILTER_NAMES.POLARITY,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.POLARITY),
          },
        },
        {
          component: 'UserFilter',
          filterType: FILTER_NAMES.UIDS,
          props: {
            selectedItems: this.activeDashboardFilters?.uids || {},
            itemId: this.card.id,
            clearable: this.displayClearable(FILTER_NAMES.UIDS),
          },
        },
        {
          component: 'DarkPostFilter',
          filterType: FILTER_NAMES.DARK_POST,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.DARK_POST),
          },
        },
        {
          component: 'CheckedPosts',
          filterType: FILTER_NAMES.FAVORITE,
        },
        {
          component: 'RepliersFilter',
          filterType: FILTER_NAMES.SAC_REPLIER,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacFilter.repliers'),
            selectItems: this.repliersToSelectItem(this.activeDashboardFilters?.repliers),
            clearable: this.displayClearable(FILTER_NAMES.SAC_REPLIER),
          },
        },
      ];
    },
  },
  watch: {
    'tempFilter.pages': {
      handler(newPages, oldPages) {
        if (!isEqual(oldPages, newPages) && Boolean(newPages?.value?.length)) {
          this.channels = Array.from(new Set(this.pagesFormatted
            .filter(({ value }) => newPages?.value.includes(value))
            .map(({ channel }) => channel)));
          this.tempFilter.channels = { value: this.channels };
        }

        if (!isEqual(oldPages, newPages) && !Boolean(newPages?.value?.length)) {
          delete this.tempFilter.channels;
          delete this.tempFilter.pages;
        }
      },
      deep: true,
    },
  },
  created() {
    this.showTikTokAlert = SHOW_TIKTOK_ALERT.some(tikTokComponentName => tikTokComponentName === this.card.component);

    EventBus.$on('showTikTokAlert', () => {
      this.showTikTokAlert = true;
    });
  },
  methods: {
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.SAC });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
    closeAlert() {
      this.showTikTokAlert = !this.showTikTokAlert;
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
